import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Chip,
  Avatar,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Link,
  LinearProgress,
  alpha,
  useTheme
} from '@mui/material';
import {
  CheckCircle,
  Warning,
  Block
} from '@mui/icons-material';
import circuitService from '../../services/circuitService';
import CircuitoViewModal from '../modals/CircuitoViewModal';
import { CaixaPort } from '../../types/caixaPort';

interface CaixaPortsTableProps {
  ports: CaixaPort[];
  loading?: boolean;
  capacidade?: number | null;
  showTitle?: boolean;
  maxHeight?: string | number;
}

const getRxPowerColor = (rxPower: number) => {
  if (rxPower >= -23 && rxPower <= 2) return 'success';
  if (rxPower >= -27 && rxPower < -23) return 'warning';
  if (rxPower < -27) return 'error';
  return 'default';
};

const getPortIcon = (status: string) => {
  switch (status) {
    case 'available':
      return <CheckCircle color="success" fontSize="small" />;
    case 'occupied':
      return <Warning color="warning" fontSize="small" />;
    case 'disabled':
      return <Block color="error" fontSize="small" />;
    default:
      return <CheckCircle fontSize="small" />;
  }
};

const getPortStatus = (port: CaixaPort) => {
  switch (port.status) {
    case 'available':
      return { label: 'Disponível', color: 'success' as const };
    case 'occupied':
      return { label: 'Ocupada', color: 'warning' as const };
    case 'disabled':
      return { label: 'Desabilitada', color: 'error' as const };
    default:
      return { label: 'Desconhecido', color: 'default' as const };
  }
};

const formatPortInfo = (port: CaixaPort) => {
  switch (port.status) {
    case 'occupied':
      return port.info?.client_full_name || port.info?.client_name || 'Cliente';
    case 'disabled':
      return port.info?.reason || 'Motivo não informado';
    default:
      return 'Disponível';
  }
};

const CaixaPortsTable: React.FC<CaixaPortsTableProps> = ({
  ports,
  loading = false,
  capacidade,
  showTitle = true,
  maxHeight = '60vh'
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [circuitoModalOpen, setCircuitoModalOpen] = useState(false);
  const [selectedCircuitoId, setSelectedCircuitoId] = useState<number | null>(null);

  const handleOpenCircuitoModal = (circuitoId: number) => {
    setSelectedCircuitoId(circuitoId);
    setCircuitoModalOpen(true);
  };

  return (
    <>
      {showTitle && (
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
          Lista de Portas ({capacidade ?? ports.length} portas)
        </Typography>
      )}

      {loading && <LinearProgress sx={{ mb: 1 }} />}

      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{
          borderRadius: 2,
          maxHeight,
          overflow: 'auto',
          boxShadow: theme.shadows[1],
          '& .MuiTable-root': { minWidth: 650 }
        }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow sx={{
              '& .MuiTableCell-head': {
                bgcolor: (t) => `color-mix(in srgb, ${theme.palette.primary.main} 20%, ${t.palette.background.default})`,
                fontWeight: 700,
                fontSize: '0.73rem',
                py: 0.5,
                borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`
              }
            }}>
              <TableCell align="center">Porta</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Informações</TableCell>
              <TableCell>Sinal RX</TableCell>
              <TableCell>Circuito</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {ports.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    {loading ? 'Carregando portas...' : 'Nenhum dado de porta disponível'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              ports.map((port) => {
                const portStatus = getPortStatus(port);
                return (
                  <TableRow key={port.port_number} hover>
                    <TableCell align="center">
                      <Avatar
                        sx={{
                          width: 25,
                          height: 25,
                          mx: 'auto',
                          bgcolor: portStatus.color === 'success' ? 'success.main'
                            : portStatus.color === 'warning' ? 'warning.main'
                              : portStatus.color === 'error' ? 'error.main'
                                : 'grey.500',
                          fontSize: '0.875rem',
                          fontWeight: 'bold'
                        }}
                      >
                        {port.port_number}
                      </Avatar>
                    </TableCell>

                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        {getPortIcon(port.status)}
                        <Chip
                          label={portStatus.label}
                          color={portStatus.color}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">
                        {port.status === 'occupied' && port.info?.client_id ? (
                          <Link
                            component="button"
                            variant="body2"
                            onClick={() => navigate(`/clients/${port.info?.client_id}`)}
                            sx={{ textDecoration: 'none', textAlign: 'left', fontWeight: 'medium' }}
                          >
                            {formatPortInfo(port)}
                          </Link>
                        ) : (
                          formatPortInfo(port)
                        )}
                      </Typography>
                      {port.status === 'occupied' && port.info?.contract_id && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Contrato:{' '}
                          <Link
                            component="button"
                            variant="caption"
                            onClick={() => navigate(`/contracts/${port.info?.contract_id}`)}
                            sx={{ textDecoration: 'none', color: 'inherit' }}
                          >
                            {port.info.contract_title}
                          </Link>
                        </Typography>
                      )}
                      {port.status === 'disabled' && port.info?.date && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Desabilitada em: {new Date(port.info.date).toLocaleString()}
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell>
                      {port.status === 'occupied'
                        && (port.info?.modelo_tipo === 'ONU' || port.info?.modelo_tipo === 'ONT')
                        && port.info?.rxPower != null ? (
                        <Chip
                          label={`${port.info.rxPower} dBm`}
                          color={getRxPowerColor(port.info.rxPower) as any}
                          size="small"
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      ) : port.status === 'occupied'
                        && (port.info?.modelo_tipo === 'ONU' || port.info?.modelo_tipo === 'ONT') ? (
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                          N/A
                        </Typography>
                      ) : null}
                    </TableCell>

                    <TableCell>
                      {port.info?.circuito_identificacao && port.info?.circuito_id ? (
                        <Chip
                          label={`Circuito ${port.info.circuito_identificacao}`}
                          color={port.status === 'occupied' ? 'error' : 'success'}
                          variant="filled"
                          size="small"
                          sx={{ fontWeight: 'medium', cursor: 'pointer' }}
                          onClick={async () => {
                            try {
                              const c = await circuitService.getCircuito(port.info!.circuito_id!);
                              handleOpenCircuitoModal(c.id);
                            } catch (e) {
                              console.error('Erro ao abrir circuito', e);
                            }
                          }}
                        />
                      ) : (
                        <Typography variant="body2">
                          {port.info?.circuito_identificacao ? `${port.info.circuito_identificacao}` : ''}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {selectedCircuitoId != null && (
        <CircuitoViewModal
          open={circuitoModalOpen}
          onClose={() => {
            setCircuitoModalOpen(false);
            setSelectedCircuitoId(null);
          }}
          circuitoId={selectedCircuitoId}
        />
      )}
    </>
  );
};

export default CaixaPortsTable;
